import {
  createAsyncThunk,
  createSelector,
  createSlice,
  isAnyOf,
  PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "app/store";
import {
  CertificateInterface,
  NewCertificateInterface,
} from "interfaces/Certificate";
import { CertificateTypeInterface } from "interfaces/CertificateType";
import { RequestStatus } from "interfaces/RequestStatus";
import { SellerInterface } from "interfaces/Seller";
import { deleteSeller, fetchSeller, fetchSellerArgument } from "./fetchSeller";
import { updateSeller } from "./updateSeller";

export enum LotOfferStatus {
  INACTIVE = "inactive",
  ACTIVE = "active",
}

export interface sellerState {
  status: RequestStatus;
  seller?: SellerInterface;
  errors:
    | {
        [key: string]: {
          [key: string]: {
            [key: string]: string[];
          };
        };
      }
    | undefined;
}

const initialState: sellerState = {
  status: RequestStatus.idle,
  seller: undefined,
  errors: undefined,
};

export const getSeller = createAsyncThunk(
  "seller/fetchSeller",
  async (args: fetchSellerArgument, { getState }) => {
    const { user } = getState() as RootState;
    if (!user || !user.role) {
      throw new Error("User role is undefined.");
    }
    const response = await fetchSeller({ ...args, endpoint: user.role });
    return {
      headers: response.headers,
      data: response.data,
      status: response.status,
    };
  }
);
export const removeSeller = createAsyncThunk(
  "seller/deleteSeller",
  async (args: fetchSellerArgument, { getState }) => {
    const { user } = getState() as RootState;
    if (!user || !user.role) {
      throw new Error("User role is undefined.");
    }
    const response = await deleteSeller({ ...args, endpoint: user.role });
    return {
      headers: response.headers,
      data: response.data,
      status: response.status,
    };
  }
);

export const patchSeller = createAsyncThunk(
  "seller/patchSeller",
  async (args, { getState }) => {
    const { user, seller } = getState() as RootState;
    if (!user || !user.role) {
      throw new Error("User role is undefined.");
    }
    if (!seller || !seller.seller) {
      throw new Error("Seller data is undefined.");
    }
    const { id, taxation_kind_id } = seller.seller;

    const response = await updateSeller({
      taxation_kind_id,
      certificates: undefined,
      id,
      endpoint: user.role,
    });
    return {
      headers: response.headers,
      data: response.data,
      status: response.status,
    };
  }
);

export const patchSellerCertificates = createAsyncThunk(
  "seller/patchSeller",
  async (args, { getState }) => {
    const { user, seller } = getState() as RootState;
    if (!user || !user.role) {
      throw new Error("User role is undefined.");
    }
    if (!seller || !seller.seller) {
      throw new Error("Seller data is undefined.");
    }
    const { id, taxation_kind_id, certificates } = seller.seller;

    const response = await updateSeller({
      taxation_kind_id,
      id,
      certificates: certificates as any as CertificateTypeInterface[],
      endpoint: user.role,
    });
    return {
      headers: response.headers,
      data: response.data,
      status: response.status,
    };
  }
);

export const sellerSlice = createSlice({
  name: "seller",
  initialState,
  reducers: {
    clear: (state) => {
      return initialState;
    },
    setTaxationKindId: (state, action) => {
      if (state.seller) {
        state.seller.taxation_kind_id = action.payload;
      }
    },
    addSellerCertificate: (
      state,
      action: PayloadAction<NewCertificateInterface>
    ) => {
      if (state.seller) {
        state.seller.certificates.push(action.payload as CertificateInterface);
      }
    },
    removeLastSellerCertificate: (state) => {
      if (state.seller && state.seller.certificates.length > 0) {
        state.seller.certificates.pop();
      }
    },
    updateSellerCertificate: (
      state,
      action: PayloadAction<NewCertificateInterface>
    ) => {
      const updatedCertificate = action.payload;
      if (!state.seller) {
        console.error("Seller data is undefined.");
        return;
      }
      const index = state.seller.certificates.findIndex(
        (certificate) => certificate.id === updatedCertificate.id
      );
      if (index >= 0 && index < state.seller.certificates.length) {
        state.seller.certificates[index] =
          updatedCertificate as CertificateInterface;
      } else {
        console.error("Invalid index for certificate update:", index);
      }
    },
    removeSellerCertificate: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!state.seller) {
        console.error("Seller data is undefined.");
        return;
      }
      const index = state.seller.certificates.findIndex(
        (certificate) => certificate.id === id
      );
      if (index >= 0 && index < state.seller.certificates.length) {
        state.seller.certificates[index]._destroy = true;
      } else {
        console.error("Invalid index for certificate removal:", index);
      }
    },
    cleanupDestroyedCertificates: (state) => {
      if (state.seller) {
        state.seller.certificates = state.seller.certificates.filter(
          (certificate) => !certificate._destroy
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSeller.fulfilled, (state, action) => {
        state.status = RequestStatus.idle;
        // console.log(action.payload.data);
        state.seller = action.payload.data.data;
        if (action.payload.data.data.errors) {
          state.errors = action.payload.data.data.errors;
        }
      })
      .addMatcher(isAnyOf(getSeller.pending), (state) => {
        state.status = RequestStatus.loading;
      })
      .addMatcher(isAnyOf(getSeller.rejected), (state) => {
        state.status = RequestStatus.failed;
      });
  },
});

export const {
  clear,
  setTaxationKindId,
  updateSellerCertificate,
  addSellerCertificate,
  removeLastSellerCertificate,
  removeSellerCertificate,
  cleanupDestroyedCertificates,
} = sellerSlice.actions;

export const selectSeller = (state: RootState): SellerInterface | undefined =>
  state.seller.seller;

export const selectSellerCertificates = (
  state: RootState
): CertificateInterface[] => state.seller.seller?.certificates || [];

// Selector to get the id parameter itself
const selectCertificateId = (_state: RootState, id: string) => id;

// Memoized selector to find a specific certificate by id
export const selectSellerCertificateById = createSelector(
  [selectSellerCertificates, selectCertificateId], // Input selectors: all certificates and the id
  (certificates, id) => {
    return certificates.find((certificate) => certificate.id === id);
  }
);

export default sellerSlice.reducer;
